import { type ReactNode } from 'react';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { Button } from '@/components/ui/button.tsx';
import { cn } from '@/lib/utils.ts';

/**
 * PageHeader — standardized page title + actions. Spec §9.5.
 *
 * Layout:
 *   md+:  [title + subtitle]                   [secondary…] [primary]
 *   sm-:  [title]
 *         [subtitle]
 *         [primary, full-width]
 *         (secondary actions collapsed into ⋯ dropdown next to primary)
 *
 * Title is the only required slot. `subtitle` is plain text or a node
 * (so callers can drop in breadcrumbs / mono identifiers). `primary` is
 * a single ReactNode — usually a Button. `secondary` is an array; on
 * mobile they collapse into an overflow `⋯` menu so the topbar doesn't
 * pile up with chips below 375px.
 */

export interface PageHeaderAction {
  label: string;
  onClick?: () => void;
  /** When omitted, an onClick is required; when present the action renders as <a>. */
  href?: string;
  icon?: ReactNode;
  /** Disable hint — typically used during a pending mutation. */
  disabled?: boolean;
  /** Mark a destructive action so the dropdown row tints red on mobile. */
  destructive?: boolean;
}

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  primary?: ReactNode;
  secondary?: readonly PageHeaderAction[];
  /** Extra className applied to the outer container (margin, sticky, etc.). */
  className?: string;
}

export function PageHeader({
  title,
  subtitle,
  primary,
  secondary,
  className,
}: PageHeaderProps) {
  const hasSecondary = secondary && secondary.length > 0;

  return (
    <div
      className={cn(
        'flex flex-col md:flex-row md:items-end md:justify-between gap-3 md:gap-4 mb-5',
        className,
      )}
    >
      <div className="min-w-0">
        {typeof title === 'string' ? (
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate">{title}</h1>
        ) : (
          title
        )}
        {subtitle ? (
          typeof subtitle === 'string' ? (
            <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
          ) : (
            <div className="text-sm text-muted-foreground mt-1">{subtitle}</div>
          )
        ) : null}
      </div>

      {primary ?? hasSecondary ? (
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Desktop: secondary actions render as Buttons inline. */}
          {hasSecondary
            ? secondary.map((action) => (
                <Button
                  key={action.label}
                  variant="outline"
                  size="sm"
                  className="hidden md:inline-flex"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  asChild={!!action.href}
                >
                  {action.href ? (
                    <a href={action.href}>
                      {action.icon}
                      {action.label}
                    </a>
                  ) : (
                    <>
                      {action.icon}
                      {action.label}
                    </>
                  )}
                </Button>
              ))
            : null}

          {primary}

          {/* Mobile: secondary actions collapse into a single ⋯ dropdown. */}
          {hasSecondary ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="md:hidden"
                  aria-label="More actions"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {secondary.map((action) => (
                  <DropdownMenuItem
                    key={action.label}
                    onClick={action.onClick}
                    disabled={action.disabled}
                    className={cn(
                      'gap-2',
                      action.destructive && 'text-destructive focus:text-destructive',
                    )}
                  >
                    {action.icon}
                    {action.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
